import React, { createContext, useContext, useState, useEffect } from 'react'
import { onAuthStateChanged, signOut, sendEmailVerification } from 'firebase/auth'
import { auth } from '../firebase/firebase'
import { checkAccountLockStatus, resetLoginAttempts } from '../firebase/accountLockoutService'

// Create the context
const AuthContext = createContext()

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext)
}

// Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accountLocked, setAccountLocked] = useState(false)
  const [lockMessage, setLockMessage] = useState('')

  // Function to resend verification email
  const resendVerificationEmail = async () => {
    if (currentUser && !currentUser.emailVerified) {
      try {
        await sendEmailVerification(currentUser)
        return { success: true, message: 'Verification email sent!' }
      } catch (error) {
        console.error('Error sending verification email:', error)
        return { success: false, error: error.message }
      }
    }
    return { success: false, error: 'No user to verify or already verified' }
  }

  // Function to check account lockout status
  const checkLockStatus = async (user) => {
    if (!user || !user.email) return { locked: false }

    try {
      const lockStatus = await checkAccountLockStatus(user.email)
      setAccountLocked(lockStatus.locked)
      setLockMessage(lockStatus.error || '')
      return lockStatus
    } catch (error) {
      console.error('Error checking lock status:', error)
      setAccountLocked(false)
      setLockMessage('')
      return { locked: false }
    }
  }

  // Function to log out the user
  const logout = () => {
    return signOut(auth)
  }

  // Set up auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Check if account is locked
        await checkLockStatus(user)

        // If account is not locked, ensure we have the latest user data
        if (!accountLocked) {
          // Reload user to get the latest emailVerified status
          await user.reload()
          setCurrentUser(auth.currentUser)
        }
      } else {
        setCurrentUser(null)
        setAccountLocked(false)
        setLockMessage('')
      }
      setLoading(false)
    })

    // Clean up subscription
    return unsubscribe
  }, [])

  // Value to be provided by the context
  const value = {
    currentUser,
    logout,
    loading,
    isEmailVerified: currentUser?.emailVerified,
    resendVerificationEmail,
    accountLocked,
    lockMessage,
    checkLockStatus,
  }

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>
}

export default AuthContext
